"use client";

import Image from "next/image";
import { CloudArrowUp } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";

import { useModalInquiryContext } from "@/context/ModalInquiryContext";
import { Link } from "@/navigation";

const HERO_IMAGE =
	"/image/new-version/ValueAddedSolutions/surface-finishing/professional-custom-surface-finishing-services.webp";

const FEATURES = ["bannerFeature1", "bannerFeature2", "bannerFeature3"] as const;

const CheckIcon = () => (
	<svg
		className="h-5 w-5 flex-shrink-0 text-themePrimary400"
		viewBox="0 0 20 20"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M4 10 Q 7 14 9 12 Q 13 8 16 5" />
	</svg>
);

export default function ServiceBanner() {
	const t = useTranslations("new-surface-finishing");
	const { openModalInquiry } = useModalInquiryContext();

	return (
		<section className="relative min-h-[400px] w-full overflow-hidden sm:min-h-[450px] md:min-h-[500px]">
			<div className="absolute inset-0 bg-[linear-gradient(180deg,_#ffffff_0%,_#ffffff_75%,_#eef6e5_90%,_#ecfed4_100%)]" />

			<div className="container relative z-10 px-4 pb-10 pt-8 font-sans sm:px-6 sm:pb-14 sm:pt-12 md:pb-16 md:pt-14 lg:pb-20 lg:pt-16">
				<div className="flex h-full w-full flex-col gap-8 lg:flex-row lg:items-center lg:gap-12">
					<div className="max-w-[600px] xl:min-w-[600px]">
						<div className="-ml-[18px] inline-block">
							<div
								className="trustpilot-widget scale-125"
								data-locale="en-US"
								data-template-id="5419b637fa0340045cd0c936"
								data-businessunit-id="68808142dd0a4c4d3e411b9c"
								data-style-height="24px"
								data-style-width="100%"
							>
								<a
									href="https://www.trustpilot.com/review/www.cncprotolabs.com?utm_medium=trustbox&utm_source=MicroTrustScore"
									target="_blank"
									rel="noopener"
								>
									trustpilotRating
								</a>
							</div>
						</div>
						<h1 className="mb-4 text-xl font-bold leading-tight text-black sm:mb-6 sm:text-[24px] md:mb-8 md:text-[30px] lg:text-[36px] xl:text-[44px]">
							{t("bannerTitle")}
						</h1>

						<ul className="mb-8 space-y-2 sm:mb-10 sm:space-y-2.5 md:mb-12 md:space-y-3">
							{FEATURES.map((featureKey) => {
								const feature = t(featureKey);
								const separatorIdx = feature.search(/[：:]/);
								const hasSeparator = separatorIdx !== -1;

								return (
									<li key={featureKey} className="flex items-start gap-2">
										<CheckIcon />
										<span className="text-xs leading-relaxed text-black sm:text-sm md:text-base">
											{hasSeparator ? (
												<>
													<strong>{feature.slice(0, separatorIdx + 1)}</strong>
													{feature.slice(separatorIdx + 1)}
												</>
											) : (
												feature
											)}
										</span>
									</li>
								);
							})}
						</ul>

						<div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
							<Link
								href="/upload"
								className="group inline-flex w-full items-center justify-center gap-2 rounded bg-themePrimary400 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:w-fit sm:px-5 sm:py-3 md:px-6 md:py-3.5 md:text-base"
							>
								<CloudArrowUp className="h-4 w-4 shrink-0 md:h-[18px] md:w-[18px]" weight="fill" />
								{t("bannerGetInstantQuote")}
							</Link>
							<button
								type="button"
								onClick={openModalInquiry}
								className="group inline-flex w-full items-center justify-center gap-2 rounded bg-mainColor px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 sm:w-fit sm:px-5 sm:py-3 md:px-10 md:py-3.5 md:text-base"
							>
								{t("bannerStartProductionQuote")}
							</button>
						</div>
					</div>

					<div className="relative w-full flex-1 lg:max-w-[600px]">
						<div className="relative mx-auto aspect-[4/2.5] max-w-[600px] overflow-hidden rounded-lg">
							<Image
								src={HERO_IMAGE}
								alt={t("bannerTitle")}
								fill
								className="object-cover"
								sizes="(min-width: 1024px) 600px, 100vw"
								priority
							/>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
